import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { fireflyGet, syncFireflyBudgets, syncFireflyBills, setAccountOpeningBalance } from '../api';
import { Button } from './Button';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Account {
  id: string;
  name: string;
  type: string;
  currency: string;
  balance: number;
}
interface Budget {
  name: string;
  currency: string;
  amount: number;
  spent: number;
}
interface Bill {
  name: string;
  currency: string;
  amount: number;
  next: string;
}
interface SectionState<T> {
  loading: boolean;
  error: string | null;
  data: T[];
}

const fmt = (n: number, cur: string) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: cur || 'INR', maximumFractionDigits: 0 }).format(n || 0);

async function loadSection<T>(
  path: string,
  query: Record<string, string> | undefined,
  transform: (a: Record<string, any>, id?: string) => T,
  setter: React.Dispatch<React.SetStateAction<SectionState<T>>>,
): Promise<void> {
  setter((s) => ({ ...s, loading: true, error: null }));
  try {
    const res = (await fireflyGet(path, query)) as { data?: Array<{ id?: string; attributes?: Record<string, any> }> };
    const rows = (res.data ?? [])
      .map((d) => (d.attributes ? transform(d.attributes, d.id) : null))
      .filter(Boolean) as T[];
    setter({ loading: false, error: null, data: rows });
  } catch (e) {
    setter({ loading: false, error: e instanceof Error ? e.message : 'Failed', data: [] });
  }
}

export function FireflyHub({ open, onClose }: Props) {
  const [accounts, setAccounts] = useState<SectionState<Account>>({ loading: true, error: null, data: [] });
  const [budgets, setBudgets] = useState<SectionState<Budget>>({ loading: true, error: null, data: [] });
  const [bills, setBills] = useState<SectionState<Bill>>({ loading: true, error: null, data: [] });
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [openingInput, setOpeningInput] = useState('');
  const [savingOpening, setSavingOpening] = useState(false);

  const load = useCallback(async () => {
    void loadSection<Account>(
      'accounts',
      undefined,
      (a, id) => ({ id: id ?? '', name: a.name, type: a.type, currency: a.currency_code, balance: Number(a.current_balance ?? 0) }),
      setAccounts,
    );
    void loadSection<Budget>(
      'budgets',
      undefined,
      (b) => ({
        name: b.name,
        currency: b.currency_code,
        amount: Number(b.limits?.[0]?.amount ?? 0),
        spent: Number(b.spent ?? 0),
      }),
      setBudgets,
    );
    void loadSection<Bill>(
      'bills',
      undefined,
      (x) => ({ name: x.name, currency: x.currency_code, amount: Number(x.amount_min ?? 0), next: x.next_expected_match ?? '' }),
      setBills,
    );
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const syncBudgets = useCallback(async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await syncFireflyBudgets();
      setSyncMsg(res.ok ? `Created ${res.count ?? 0} budget(s).` : (res.error ?? 'Failed'));
      void load();
    } catch (e) {
      setSyncMsg(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSyncing(false);
    }
  }, [load]);

  const syncBills = useCallback(async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await syncFireflyBills();
      if (res.ok) {
        const n = res.created?.length ?? 0;
        setSyncMsg(n > 0 ? `Created ${n} bill(s) from recurring spend.` : 'No recurring debits detected.');
      } else {
        setSyncMsg(res.error ?? 'Failed');
      }
      void load();
    } catch (e) {
      setSyncMsg(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSyncing(false);
    }
  }, [load]);

  if (!open) return null;

  const Section = <T,>({ title, state, render }: { title: string; state: SectionState<T>; render: (row: T) => React.ReactNode }) => (
    <div className="rounded-xl border border-zinc-800/60 bg-zinc-900/30 p-4">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">{title}</h3>
      {state.loading ? (
        <p className="text-xs text-zinc-500">Loading…</p>
      ) : state.error ? (
        <p className="text-xs text-rose-400">{state.error}</p>
      ) : state.data.length === 0 ? (
        <p className="text-xs text-zinc-600">Nothing found in your Firefly instance.</p>
      ) : (
        <div className="space-y-1.5">
          {state.data.map((row, i) => (
            <div key={i} className="rounded-lg border border-zinc-800/60 bg-zinc-900/50 px-3 py-2 text-xs text-zinc-300">
              {render(row)}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const saveOpening = useCallback(
    async (id: string) => {
      const amt = Number(openingInput);
      if (!Number.isFinite(amt)) return;
      setSavingOpening(true);
      try {
        await setAccountOpeningBalance(id, amt);
        setEditingId(null);
        setOpeningInput('');
        void load();
      } catch (e) {
        setSyncMsg(e instanceof Error ? e.message : 'Failed');
      } finally {
        setSavingOpening(false);
      }
    },
    [openingInput, load],
  );

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/70 backdrop-blur-sm p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div
        className="my-0 w-full max-w-3xl rounded-t-2xl border border-zinc-800 bg-[#0c0c0f] shadow-2xl sm:my-8 sm:rounded-2xl max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-zinc-800/80 px-6 py-4">
          <div>
            <h2 className="text-base font-bold tracking-tight text-zinc-100">Firefly III features</h2>
            <p className="mt-0.5 text-xs text-zinc-500">Read-only view of your Firefly data</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-700/60 text-zinc-400 transition hover:border-zinc-600 hover:bg-zinc-800/60 hover:text-zinc-200"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
          <div className="rounded-xl border border-zinc-800/60 bg-zinc-900/30 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Accounts & Net Worth</h3>
              {!accounts.loading && !accounts.error && accounts.data.length > 0 && (
                <span className="text-xs font-semibold text-emerald-400">
                  Net Worth {fmt(accounts.data.reduce((s, a) => s + a.balance, 0), accounts.data[0].currency)}
                </span>
              )}
            </div>
            {accounts.loading ? (
              <p className="text-xs text-zinc-500">Loading…</p>
            ) : accounts.error ? (
              <p className="text-xs text-rose-400">{accounts.error}</p>
            ) : accounts.data.length === 0 ? (
              <p className="text-xs text-zinc-600">No accounts in your Firefly instance.</p>
            ) : (
              <div className="space-y-2">
                {accounts.data.map((a) => (
                  <div key={a.id} className="rounded-lg border border-zinc-800/60 bg-zinc-900/50 px-3 py-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-zinc-100">{a.name}</p>
                        <p className="text-[10px] uppercase tracking-wide text-zinc-600">{a.type}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${a.balance >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {fmt(a.balance, a.currency)}
                        </span>
                        <button
                          type="button"
                          onClick={() => { setEditingId(a.id); setOpeningInput(String(Math.max(0, a.balance))); }}
                          className="rounded-md border border-zinc-700 px-1.5 py-0.5 text-[10px] text-zinc-400 hover:text-zinc-200"
                        >
                          Set opening balance
                        </button>
                      </div>
                    </div>
                    {editingId === a.id && (
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          type="number"
                          value={openingInput}
                          onChange={(e) => setOpeningInput(e.target.value)}
                          placeholder="Opening balance"
                          className="w-32 rounded-md border border-zinc-700 bg-zinc-800 px-2 py-1 text-xs text-zinc-100 outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => void saveOpening(a.id)}
                          disabled={savingOpening}
                          className="rounded-md bg-emerald-500 px-2 py-1 text-[10px] font-semibold text-black hover:bg-emerald-400 disabled:opacity-50"
                        >
                          {savingOpening ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" onClick={() => setEditingId(null)} className="text-[10px] text-zinc-500 hover:text-zinc-300">
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <Section
            title="Budgets"
            state={budgets}
            render={(b: Budget) => (
              <div className="flex items-center justify-between">
                <p className="font-medium text-zinc-100">{b.name}</p>
                <span className="text-zinc-300">
                  {fmt(b.spent, b.currency)} <span className="text-zinc-600">/ {fmt(b.amount, b.currency)}</span>
                </span>
              </div>
            )}
          />
          <div className="flex items-center justify-between gap-2 md:col-span-2">
            <p className="text-[11px] text-zinc-500">
              Create a yearly budget per spending category from this year's WalletPulse spend.
              {syncMsg ? <span className="ml-2 text-emerald-400">{syncMsg}</span> : null}
            </p>
            <Button variant="secondary" size="sm" onClick={() => void syncBudgets()} disabled={syncing}>
              {syncing ? 'Creating…' : 'Populate budgets'}
            </Button>
          </div>
          <div className="md:col-span-2">
            <Section
              title="Bills (recurring)"
              state={bills}
              render={(b: Bill) => (
                <div className="flex items-center justify-between">
                  <p className="font-medium text-zinc-100">{b.name}</p>
                  <span className="text-zinc-300">
                    {fmt(b.amount, b.currency)}
                    {b.next ? <span className="ml-2 text-[10px] text-zinc-500">next {b.next}</span> : null}
                  </span>
                </div>
              )}
            />
            <div className="mt-3 flex items-center justify-between gap-2">
              <p className="text-[11px] text-zinc-500">
                Detect repeating merchants and create Firefly bills with the next due date.
              </p>
              <Button variant="secondary" size="sm" onClick={() => void syncBills()} disabled={syncing}>
                {syncing ? 'Detecting…' : 'Detect bills'}
              </Button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-zinc-800/80 px-6 py-3">
          <p className="text-[10px] text-zinc-600">Create / edit these in your Firefly III instance.</p>
          <Button variant="ghost" size="sm" onClick={() => void load()}>Refresh</Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
